import StripeJS from "stripe";

import { ViewWLSubscriptionsTiers } from "../../../Common/models/subscriptions";
import { Promo } from "../../../Common/models/promos";
import { SignInProvider } from "../../../Common/models/user";
import { ReferralConfig } from "../../../Common/models/config";
import { createWithEqualityFn } from "zustand/traditional";

interface ReferredBonusEntries {
  referedBonusEntries: {
    title: string;
    value: number;
    promoText: string;
  };
  twoWayBonusEntries: {
    title: string;
    value: number;
    promoText: string;
  };
  referedTotalEntries: number;
}

export const PAYMENT_ELEMENT_NOT_READY_MESSAGE =
  "Form is not yet ready. Please try again.";

export const defaultValidatePaymentElement = async (): Promise<string | null> =>
  PAYMENT_ELEMENT_NOT_READY_MESSAGE;

interface WebSignUpState {
  emailAddress: string;
  setEmailAddress: (value: string) => void;

  signInProvider: SignInProvider | null;
  setSignInProvider: (state: SignInProvider) => void;

  firstName: string;
  setFirstName: (value: string) => void;

  lastName: string;
  setLastName: (value: string) => void;

  phoneNumber: string;
  setPhoneNumber: (value: string) => void;

  userState: string;
  setUserState: (value: string) => void;

  stripeId: string;
  setStripeId: (value: string) => void;

  userId: string;
  setUserId: (value: string) => void;

  subscriptionList: ViewWLSubscriptionsTiers[];
  setSubscriptionList: (value: ViewWLSubscriptionsTiers[]) => void;

  selectedSubscription: undefined | null | ViewWLSubscriptionsTiers;
  setSelectedSubscription: (value: null | ViewWLSubscriptionsTiers) => void;

  /** True when mount prereqs were hydrated from query bootstrap params. */
  bootstrapFromQuery: boolean;
  setBootstrapFromQuery: (value: boolean) => void;

  validPromoCode: string | undefined;
  setValidPromoCode: (value: string | undefined) => void;

  validPromoCodePromo: Promo | null | undefined;
  setValidPromoCodePromo: (value: Promo | null | undefined) => void;

  promoEntries: undefined | number;
  setPromoEntries: (value: undefined | number) => void;

  lockerWinEntries: undefined | number;
  setLockerWinEntries: (value: undefined | number) => void;

  referredBonusEntries: undefined | ReferredBonusEntries;
  setReferredBonusEntries: (value: undefined | ReferredBonusEntries) => void;

  referralConfig: undefined | null | ReferralConfig;
  setReferralConfig: (value: undefined | null | ReferralConfig) => void;

  validStripeCoupon: undefined | null | StripeJS.Coupon;
  setValidStripeCoupon: (value: StripeJS.Coupon | null | undefined) => void;

  stripeClientSecretId: undefined | null | string;
  setStripeClientSecretId: (value: undefined | null | string) => void;

  subscriptionIdToPay: undefined | null | string;
  setSubscriptionIdToPay: (value: undefined | null | string) => void;

  /** Identifies the signup input the cached checkout session above belongs to. */
  checkoutSessionKey: string | null;
  setCheckoutSessionKey: (value: string | null) => void;

  submitPayment: () => Promise<void>;
  setSubmitPayment: (func: () => Promise<void>) => void;

  validatePaymentElement: () => Promise<string | null>;
  setValidatePaymentElement: (func: () => Promise<string | null>) => void;
}

const useWebSignupStore = createWithEqualityFn<WebSignUpState>((set) => ({
  emailAddress: "",
  setEmailAddress: (emailAddress) => set({ emailAddress }),

  signInProvider: null,
  setSignInProvider: (signInProvider) => set({ signInProvider }),

  firstName: "",
  setFirstName: (firstName) => set({ firstName }),

  lastName: "",
  setLastName: (lastName) => set({ lastName }),

  phoneNumber: "",
  setPhoneNumber: (phoneNumber) => set({ phoneNumber }),

  userState: "",
  setUserState: (userState) => set({ userState }),

  stripeId: "",
  setStripeId: (stripeId) => set({ stripeId }),

  userId: "",
  setUserId: (userId) => set({ userId }),

  subscriptionList: [],
  setSubscriptionList: (subscriptionList) => set({ subscriptionList }),

  selectedSubscription: undefined,
  setSelectedSubscription: (selectedSubscription) =>
    set({ selectedSubscription }),

  bootstrapFromQuery: false,
  setBootstrapFromQuery: (bootstrapFromQuery) => set({ bootstrapFromQuery }),

  validPromoCode: undefined,
  setValidPromoCode: (validPromoCode) => set({ validPromoCode }),

  validPromoCodePromo: undefined,
  setValidPromoCodePromo: (validPromoCodePromo) => set({ validPromoCodePromo }),

  promoEntries: undefined,
  setPromoEntries: (promoEntries) => set({ promoEntries }),

  lockerWinEntries: undefined,
  setLockerWinEntries: (lockerWinEntries) => set({ lockerWinEntries }),

  referredBonusEntries: undefined,
  setReferredBonusEntries: (referredBonusEntries) =>
    set({ referredBonusEntries }),

  referralConfig: undefined,
  setReferralConfig: (referralConfig) => set({ referralConfig }),

  validStripeCoupon: undefined,
  setValidStripeCoupon: (validStripeCoupon) => set({ validStripeCoupon }),

  stripeClientSecretId: undefined,
  setStripeClientSecretId: (stripeClientSecretId) =>
    set({ stripeClientSecretId }),

  subscriptionIdToPay: undefined,
  setSubscriptionIdToPay: (subscriptionIdToPay) => set({ subscriptionIdToPay }),

  checkoutSessionKey: null,
  setCheckoutSessionKey: (checkoutSessionKey) => set({ checkoutSessionKey }),

  submitPayment: async () => {},
  setSubmitPayment: (submitPayment) => set({ submitPayment }),

  validatePaymentElement: defaultValidatePaymentElement,
  setValidatePaymentElement: (validatePaymentElement) =>
    set({ validatePaymentElement }),
}));

export default useWebSignupStore;
